import { useState, useEffect, useRef, useContext } from 'react';
import * as Sentry from '@sentry/browser';
import { AppContext } from 'providers';
import { useDirectUpload } from 'hooks';
import { Carousel } from 'ui/components/Carousel';
import { ModalBody, ModalHeader, ModalFooter, Flex, Text } from '@chakra-ui/react';
import { CbButton } from 'ui/components/Buttons/CbButton';
import { DragAndDropInput } from 'ui/components/DragAndDropInput';
import ArrowLeft from '../../Icons/ArrowLeft';
import { FileChip } from 'ui/components/FileChip';
import styles from './CbFileUpload.module.css';
const ACCEPTABLE_FILE_TYPES = ['.docx', '.doc', '.pdf', '.txt', '.csv', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.mp4', '.mov', '.avi', '.wmv', '.mp3', '.wav', '.ogg', '.flac', '.bmp', '.tiff', '.webp', '.odt', '.ods', '.odp', '.aac', '.mkv', '.webm'];
export const CbFileUpload = ({
  onReturnToCreatePost,
  fileData,
  setFileData,
  removeFile
}) => {
  const {
    setToast
  } = useContext(AppContext);
  const [imageFiles, setImageFiles] = useState<string[]>([]);
  const [otherFiles, setOtherFiles] = useState<File[]>([]);
  const {
    uploadFiles
  } = useDirectUpload();
  const inputRef = useRef<HTMLInputElement>(null);
  const files = fileData.map(data => data.file);
  useEffect(() => {
    handleImageFiles(files);
    handleOtherFiles(files);
    return () => {
      imageFiles.forEach(file => URL.revokeObjectURL(file));
    };
  }, [fileData]);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files as FileList);
    uploadFiles(files).then(response => {
      setFileData(previousFiles => [...previousFiles, ...response]);
    }).catch(error => {
      console.log(error);
      Sentry.captureException(e, {
        extra: {
          message: 'Error with file upload'
        }
      });
      setToast({
        title: 'Error',
        description: 'There was an error uploading your files',
        status: 'error'
      });
    });
  };
  const handleImageFiles = files => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    setImageFiles(imageFiles);
  };
  const handleOtherFiles = files => {
    const otherFiles = files.filter(file => !file.type.startsWith('image/'));
    setOtherFiles(otherFiles);
  };
  return <>
      <ModalHeader className={styles.header} data-sentry-element="ModalHeader" data-sentry-source-file="CbFileUpload.tsx">
        <ArrowLeft onClick={onReturnToCreatePost} data-sentry-element="ArrowLeft" data-sentry-source-file="CbFileUpload.tsx" />
        <div style={{
        display: 'flex',
        flexDirection: 'column'
      }}>
          <Text className={styles.title} data-sentry-element="Text" data-sentry-source-file="CbFileUpload.tsx">Add Files</Text>
          <Text className={styles.subtitle} data-sentry-element="Text" data-sentry-source-file="CbFileUpload.tsx">{files.length} Total Files</Text>
        </div>
      </ModalHeader>
      <ModalBody data-sentry-element="ModalBody" data-sentry-source-file="CbFileUpload.tsx">
        {imageFiles.length > 0 && <Carousel images={imageFiles} onRemove={removeFile} />}
        {otherFiles.length > 0 && <div className={styles.otherFiles}>
            {otherFiles.map(file => <FileChip file={file} removeFile={removeFile} />)}
          </div>}
        {files.length === 0 && <DragAndDropInput label="Drag and drop files here or click to upload" onUpload={handleFileChange}
      // Accepts standard file types
      InputProps={{
        multiple: true,
        accept: ACCEPTABLE_FILE_TYPES.join(',')
      }} inputRef={inputRef} id="newsfeed-attachment" />}
      </ModalBody>
      <ModalFooter className={styles.footer} data-sentry-element="ModalFooter" data-sentry-source-file="CbFileUpload.tsx">
        <Flex justifyContent="space-between" width="100%" data-sentry-element="Flex" data-sentry-source-file="CbFileUpload.tsx">
          {files.length > 0 ? <>
              <CbButton onClick={() => inputRef?.current?.click()} disabled={!inputRef?.current}>
                Add More Files
              </CbButton>
              <input type="file" ref={inputRef} onChange={handleFileChange} className={styles.fileInput} multiple={true} accept={ACCEPTABLE_FILE_TYPES.join(',')} />
            </> : <span />}
          <CbButton onClick={onReturnToCreatePost} data-sentry-element="CbButton" data-sentry-source-file="CbFileUpload.tsx">Done</CbButton>
        </Flex>
      </ModalFooter>
    </>;
};